/* LT-Prelims-01 — Prelim Library list page.
 *
 * LT-43c model: the library has a DEFAULT (working) set that seeds onto every
 * new tender, plus a larger POOL of less-used items. The main list shows only
 * the default set. "Add more items" opens the pool so an estimator can pull an
 * item into the defaults; "Remove" sends an item back to the pool (reversible —
 * the old irreversible "archive" is gone). Permanent delete removes for good.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, ListChecks, ChevronRight, ChevronDown, X, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp } from "../lib/format";
import { PageHeader, Card, PrimaryButton, EmptyState } from "../components/UI";

const cls = (...a) => a.filter(Boolean).join(" ");

const SECTION_LABEL = {
  staff: "Staff",
  welfare: "Welfare",
  plant: "Plant, Materials Handling & Attendances",
  scaffolding: "Scaffolding — Internal & External",
  fencing: "Temporary Fencing, Hoarding & Security",
  cleaning: "Cleaning",
  misc: "Misc Items",
};
const SECTION_ORDER = ["staff", "welfare", "plant", "scaffolding", "fencing", "cleaning", "misc"];

const CATEGORY_LABEL = {
  scaffolding: "Scaffolding",
  welfare: "Welfare Facilities",
  management: "Management",
  sundries: "Sundries",
};

const UNIT_LABEL = {
  weekly: "weekly",
  item: "item",
  percentage_weekly: "% × weekly",
  nr_weeks: "qty × weeks",
  qty_only: "qty × rate",
  percentage_section: "% of section",
};

const isDefault = (it) => it.is_default_for_new_tenders !== false;

function groupItems(items, q) {
  const needle = (q || "").trim().toLowerCase();
  const matches = (it) => !needle
    || (it.item_name || "").toLowerCase().includes(needle)
    || (it.section || "").toLowerCase().includes(needle);
  const map = {};
  for (const it of items) {
    if (!matches(it)) continue;
    (map[it.section] ||= []).push(it);
  }
  for (const k in map) {
    map[k].sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)
        || (a.item_name || "").localeCompare(b.item_name || ""),
    );
  }
  const ordered = SECTION_ORDER.filter((k) => map[k]).map((k) => ({
    key: k, label: SECTION_LABEL[k] || k, items: map[k],
  }));
  Object.keys(map).filter((k) => !SECTION_ORDER.includes(k)).sort()
    .forEach((k) => ordered.push({ key: k, label: k, items: map[k] }));
  return ordered;
}

export default function PrelimLibrary() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [poolOpen, setPoolOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/prelim-library")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load prelim library"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const defaults = useMemo(() => items.filter(isDefault), [items]);
  const pool = useMemo(() => items.filter((it) => !isDefault(it)), [items]);

  const grouped = useMemo(() => groupItems(defaults, q), [defaults, q]);
  const totalFiltered = grouped.reduce((s, g) => s + g.items.length, 0);
  const isExpanded = (key) => !!q.trim() || !collapsed[key];
  const toggleGroup = (key) => setCollapsed((p) => ({ ...p, [key]: !p[key] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => {
    const all = {};
    grouped.forEach((g) => { all[g.key] = true; });
    setCollapsed(all);
  };

  const setDefaultFlag = async (item, value) => {
    // Optimistic — move between defaults / pool immediately.
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, is_default_for_new_tenders: value } : x)));
    try {
      await api.put(`/prelim-library/${item.id}`, { is_default_for_new_tenders: value });
      toast.success(value ? `Added “${item.item_name}” to defaults` : `Removed “${item.item_name}” from defaults`);
    } catch (e) {
      console.error(e);
      toast.error("Update failed");
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, is_default_for_new_tenders: !value } : x)));
    }
  };

  const deletePermanent = async (item) => {
    if (!window.confirm(`Permanently delete “${item.item_name}”? This can't be undone. Existing tenders are unaffected.`)) return;
    setItems((prev) => prev.filter((x) => x.id !== item.id));
    try {
      await api.delete(`/prelim-library/${item.id}/permanent`);
      toast.success("Deleted");
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
      load();
    }
  };

  return (
    <div className="app-container py-8">
      <PageHeader
        title="Prelim Library"
        subtitle="Your default prelim set seeds onto every new tender. Add less-used items from the full library when a job needs them — nothing is ever lost."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPoolOpen(true)}
              data-testid="prelim-library-add-more"
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2 transition-colors"
              style={{ fontSize: 12.5, fontWeight: 500 }}
            >
              <Plus size={13} /> Add more items
              {pool.length > 0 && (
                <span className="ml-1 rounded-full bg-surface-2 text-ink-3 px-1.5" style={{ fontSize: 10.5 }}>
                  {pool.length}
                </span>
              )}
            </button>
            <PrimaryButton icon={Plus} onClick={() => navigate("/prelim-library/new")}>Add prelim item</PrimaryButton>
          </div>
        }
      />

      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search default items by name or section…" spellCheck="true"
            data-testid="prelim-library-search"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            style={{ fontSize: 13 }}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-ink-3" style={{ fontSize: 11.5 }} data-testid="prelim-library-counts">
            {totalFiltered} default item{totalFiltered === 1 ? "" : "s"} · {pool.length} more in library
          </span>
          <button onClick={expandAll} data-testid="prelim-library-expand-all"
            className="h-7 px-2.5 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2 transition-colors"
            style={{ fontSize: 11.5 }}>Expand all</button>
          <button onClick={collapseAll} data-testid="prelim-library-collapse-all"
            className="h-7 px-2.5 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2 transition-colors"
            style={{ fontSize: 11.5 }}>Collapse all</button>
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : defaults.length === 0 ? (
          <EmptyState
            icon={ListChecks}
            title="No default prelim items"
            hint={pool.length > 0
              ? "Your defaults are empty — click “Add more items” to pull items from the library, or add a new one."
              : "Add a prelim item to start your default set."}
            action={<PrimaryButton icon={Plus} onClick={() => navigate("/prelim-library/new")}>Add prelim item</PrimaryButton>}
          />
        ) : grouped.length === 0 ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12.5 }}>No matching items.</div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-2/50">
                <th className="col-head px-4 py-2.5">Item name</th>
                <th className="col-head px-4 py-2.5" style={{ width: 130 }}>Category</th>
                <th className="col-head px-4 py-2.5" style={{ width: 130 }}>Unit type</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 130 }}>Default rate</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 100 }}>Default qty / %</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <React.Fragment key={g.key}>
                  <tr
                    onClick={() => toggleGroup(g.key)}
                    data-testid={`prelim-library-group-${g.key}`}
                    className="cursor-pointer hover:bg-surface-2/40 transition-colors border-b border-line-soft"
                    style={{ background: "#F5F3EE" }}
                  >
                    <td colSpan={6} className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {isExpanded(g.key)
                          ? <ChevronDown size={12} className="text-ink-3" />
                          : <ChevronRight size={12} className="text-ink-3" />}
                        <span className="font-mono uppercase text-ink-2"
                          style={{ fontSize: 10.5, letterSpacing: "0.06em", fontWeight: 700 }}>
                          {g.label}
                        </span>
                        <span className="text-ink-3" style={{ fontSize: 11 }}>({g.items.length})</span>
                      </div>
                    </td>
                  </tr>
                  {isExpanded(g.key) && g.items.map((m) => (
                    <tr key={m.id}
                      onClick={() => navigate(`/prelim-library/${m.id}`)}
                      data-testid={`prelim-library-row-${m.id}`}
                      className="group cursor-pointer hover:bg-surface-2/60 border-b border-line-soft">
                      <td className="px-4 py-3">
                        <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>
                          {m.item_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono uppercase text-ink-3"
                          style={{ fontSize: 10, letterSpacing: "0.05em" }}>
                          {CATEGORY_LABEL[m.client_category] || m.client_category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono uppercase text-ink-3"
                          style={{ fontSize: 10, letterSpacing: "0.05em" }}>
                          {UNIT_LABEL[m.unit_type] || m.unit_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono tnum text-ink" style={{ fontSize: 12.5 }}>
                          {gbp(m.default_rate || 0)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono tnum text-ink-3" style={{ fontSize: 11.5 }}>
                          {m.default_quantity != null ? m.default_quantity
                            : m.default_percentage != null ? `${m.default_percentage}%`
                            : "—"}
                        </span>
                      </td>
                      <td className="pr-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setDefaultFlag(m, false)}
                          data-testid={`prelim-library-remove-${m.id}`}
                          title="Remove from defaults — keeps it in the library"
                          className="inline-flex items-center gap-1 text-ink-3 hover:text-note-red transition-colors"
                          style={{ fontSize: 11.5 }}
                        >
                          <X size={12} /> Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {poolOpen && (
        <PoolModal
          pool={pool}
          onClose={() => setPoolOpen(false)}
          onAdd={(item) => setDefaultFlag(item, true)}
          onDelete={deletePermanent}
        />
      )}
    </div>
  );
}

function PoolModal({ pool, onClose, onAdd, onDelete }) {
  const [q, setQ] = useState("");
  const grouped = useMemo(() => groupItems(pool, q), [pool, q]);
  const total = grouped.reduce((s, g) => s + g.items.length, 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(20,18,14,0.45)" }}
      onClick={onClose}
      data-testid="prelim-library-pool-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[82vh] flex flex-col rounded-lg border border-line bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-line">
          <div>
            <h2 className="text-ink" style={{ fontSize: 15, fontWeight: 600 }}>Add more library items</h2>
            <p className="text-ink-3" style={{ fontSize: 11.5 }}>
              Less-used items. Add one to include it in your default set (seeds onto new tenders).
            </p>
          </div>
          <button onClick={onClose} data-testid="prelim-library-pool-close" className="text-ink-4 hover:text-ink p-1 rounded">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-line-soft">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search the library…" spellCheck="true"
              data-testid="prelim-library-pool-search"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
              style={{ fontSize: 13 }}
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-2 py-2">
          {pool.length === 0 ? (
            <div className="py-12 text-center text-ink-3" style={{ fontSize: 12.5 }}>
              All library items are already in your defaults.
            </div>
          ) : total === 0 ? (
            <div className="py-12 text-center text-ink-3" style={{ fontSize: 12.5 }}>No matching items.</div>
          ) : (
            grouped.map((g) => (
              <div key={g.key} className="mb-2">
                <div className="px-3 py-1.5">
                  <span className="font-mono uppercase text-ink-3" style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700 }}>
                    {g.label}
                  </span>
                </div>
                {g.items.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`prelim-library-pool-row-${m.id}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-surface-2/60"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-ink truncate" style={{ fontSize: 13, fontWeight: 500 }}>{m.item_name}</div>
                      <div className="text-ink-4 font-mono uppercase" style={{ fontSize: 9.5, letterSpacing: "0.05em" }}>
                        {CATEGORY_LABEL[m.client_category] || m.client_category} · {UNIT_LABEL[m.unit_type] || m.unit_type} · {gbp(m.default_rate || 0)}
                      </div>
                    </div>
                    <button
                      onClick={() => onDelete(m)}
                      data-testid={`prelim-library-pool-delete-${m.id}`}
                      title="Delete permanently"
                      className="text-ink-4 hover:text-note-red p-1.5 rounded"
                    >
                      <Trash2 size={13} />
                    </button>
                    <button
                      onClick={() => onAdd(m)}
                      data-testid={`prelim-library-pool-add-${m.id}`}
                      className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-accent text-white hover:opacity-90 transition-opacity"
                      style={{ fontSize: 12, fontWeight: 500 }}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
